/**
 * useBranchScope — WHICH branch's books am I looking at.
 *
 * This answers a different question from `useDimensions`, and confusing the two
 * is the whole reason this exists:
 *
 *   - `useDimensions` is for a POSTING form: "which branch does this new invoice
 *     belong to." It writes `transactions.branch_id`. It is data entry.
 *   - this is the READING lens: "whose figures am I on screen right now."
 *     It writes nothing. It filters.
 *
 * Before this, the lens had no home. Branch was a filter that each report
 * re-invented and each screen forgot, so a business with two units could not
 * tell from the page which unit's numbers it was reading. Founder, 19 Aug 2026,
 * looking at a client with two: *"how will he know which branch is he currently
 * looking at… i think there has to be something on the top left corner which
 * helps us choose."* So the lens is org-level state, shown beside the org name,
 * and every reading surface takes it from here rather than growing its own.
 *
 * `scopeId === null` means ALL branches, consolidated. That is the default and
 * the honest one: a business reads its whole self unless it says otherwise.
 *
 * Three guards this file exists to hold:
 *
 *   1. **A stale id falls back to consolidated, never to empty.** The choice is
 *      remembered per org across reloads. A branch that has since been deleted,
 *      renamed away, or put out of the user's reach would otherwise filter every
 *      figure to nothing while the header cheerfully named it — a zero that
 *      looks like a fact. `load()` drops any id the branch list does not carry.
 *   2. **The scope never leaks across orgs.** Remembered as a per-org map, and
 *      `load()` for a new org replaces the whole slice, so a branch id from one
 *      business can never be applied to another's books.
 *   3. **A branch-restricted member is LOCKED, and told so.** `my_branch_scope`
 *      + the RESTRICTIVE RLS of migration 00234 mean such a user physically
 *      cannot read another branch. Offering them "All branches" would render a
 *      single branch's figures under a label claiming to be everything, which is
 *      worse than not offering it. `locked` says which case we are in.
 *
 * @since brctx-1 (2026-08-19)
 */
import { create } from 'zustand';
import { supabase } from '@/db/supabase';

export interface ScopeBranch {
    id: string;
    name: string;
    is_default: boolean;
    gstin: string | null;
    gst_registration_id: string | null;
}

interface BranchScopeState {
    /** The org these branches belong to. Guards against cross-org application. */
    orgId: string | null;
    branches: ScopeBranch[];
    loading: boolean;
    /** `null` = all branches, consolidated. */
    scopeId: string | null;
    /** True when RBAC pins this member to one branch — no consolidated view exists for them. */
    locked: boolean;
    load: (orgId: string | null | undefined) => Promise<void>;
    setScope: (branchId: string | null) => void;
}

const STORE_KEY = 'karr_branch_scope';

function readRemembered(): Record<string, string> {
    try {
        const raw = localStorage.getItem(STORE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed as Record<string, string> : {};
    } catch { return {}; }
}

function remember(orgId: string, branchId: string | null) {
    try {
        const all = readRemembered();
        if (branchId) all[orgId] = branchId; else delete all[orgId];
        localStorage.setItem(STORE_KEY, JSON.stringify(all));
    } catch { /* private mode / storage off — the lens just resets next visit */ }
}

export const useBranchScope = create<BranchScopeState>((set, get) => ({
    orgId: null,
    branches: [],
    loading: false,
    scopeId: null,
    locked: false,

    load: async (orgId) => {
        if (!orgId) {
            set({ orgId: null, branches: [], scopeId: null, locked: false, loading: false });
            return;
        }
        // fastphone-2: a read already on its way for this org serves every caller.
        if (get().orgId === orgId && (get().loading || get().branches.length > 0)) return;
        set({ loading: true, orgId, branches: [], scopeId: null, locked: false });

        const [{ data: brData }, { data: pinned }] = await Promise.all([
            // addedonce: the one branch list, loaded with the read so the register stays out of the first load
            import('@/shared/entities/useEntity').then(({ readEntityResult }) => readEntityResult('branch', orgId, (rows) => rows.filter((r) => r.is_active !== false).sort((x, y) => (Number(y.is_default ?? 0) - Number(x.is_default ?? 0))).map((r) => ({ id: r.id, name: r.name, is_default: r.is_default, gstin: r.gstin, gst_registration_id: r.gst_registration_id })))),
            supabase.rpc('my_branch_scope', { p_org_id: orgId }),
        ]);

        // A later org switch may have landed while this was in flight. Applying
        // these rows now would put one org's branches under another's name.
        if (get().orgId !== orgId) return;

        let rows = (brData ?? []) as ScopeBranch[];
        const lockedTo = typeof pinned === 'string' && pinned ? pinned : null;
        if (lockedTo) rows = rows.filter((b) => b.id === lockedTo);

        // Guard 3: pinned members have no consolidated view to offer.
        // Guard 1: a remembered id the list does not carry falls back to consolidated.
        const remembered = readRemembered()[orgId] ?? null;
        const scopeId = lockedTo
            ?? (remembered && rows.some((b) => b.id === remembered) ? remembered : null);
        if (remembered && scopeId !== remembered) remember(orgId, null);

        set({ branches: rows, scopeId, locked: Boolean(lockedTo), loading: false });
    },

    setScope: (branchId) => {
        const { orgId, branches, locked } = get();
        if (locked || !orgId) return;
        if (branchId && !branches.some((b) => b.id === branchId)) return;
        remember(orgId, branchId);
        set({ scopeId: branchId });
    },
}));

/** The short label for the control itself. */
export function branchScopeLabel(branches: ScopeBranch[], scopeId: string | null): string {
    if (!scopeId) return 'All branches';
    return branches.find((b) => b.id === scopeId)?.name ?? 'All branches';
}

/**
 * WHICH books a downloaded document covers, in prose, for stamping onto a PDF or
 * an Excel sheet.
 *
 * Consolidated NAMES the branches it consolidated rather than saying "all",
 * because a reader holding the file cannot otherwise tell whether the list they
 * are imagining is the list Karr used. This mirrors `scopeLine` in Reports,
 * which the founder asked for on 12 Aug 2026 after a branch-only Balance Sheet
 * proved indistinguishable from the consolidated one once emailed.
 *
 * Returns `undefined` only when there is genuinely no scope to state — a
 * single-branch business reading its whole self.
 */
export function branchScopeLine(branches: ScopeBranch[], scopeId: string | null): string | undefined {
    if (scopeId) {
        const b = branches.find((x) => x.id === scopeId);
        return `Branch: ${b?.name ?? 'selected branch'} only`;
    }
    // Nothing to state for a business that has one branch, or none: a line
    // saying so would be noise on every document Karr prints. But an EXPLICIT
    // scope is always stated, even when the list holds one branch — that is a
    // branch-restricted member, whose balance sheet really is one branch's and
    // must not travel as if it were the whole entity's.
    if (branches.length < 2) return undefined;
    return `All branches, consolidated: ${branches.map((b) => b.name).join(', ')}`;
}
